import toastr from 'toastr';
import { trackRegistrationSuccess } from '../tracking/registration-success';

document.addEventListener('DOMContentLoaded', () => {

    // Configuration - use dynamic root URL from localized data
    const CONFIG = {
        selectors: '.subscribe-form-single, .popup-single-post-form, .subscribe-form-main-t3',
        priceFormSelector: '.from-dk-service-sigle .signup-form-popupForm-v2 form',
        successModalSelector: '.thank-you-subscribe-modal',
        redirectDelay: 500,
        getSuccessUrl: () => (window.subscribePopupData?.root_url || window.location.origin) + '/dang-ky-thanh-cong'
    };

    let modalTrigger = null;

    /**
     * Đóng popup đăng ký thành công
     */
    const closeSuccessModal = () => {
        const modal = document.querySelector(CONFIG.successModalSelector);
        if (!modal) {
            return;
        }

        modal.classList.remove('show-modal');
        modal.setAttribute('aria-hidden', 'true');
        modalTrigger?.focus();
        modalTrigger = null;
    };

    /**
     * Hiển thị popup đăng ký thành công
     *
     * @param {HTMLElement|null} trigger Phần tử đã mở popup.
     */
    const showSuccessModal = (trigger = null) => {
        const modal = document.querySelector(CONFIG.successModalSelector);
        if (!modal) {
            toastr.error('Không tìm thấy nội dung đăng ký thành công.');
            return;
        }

        modalTrigger = trigger;
        modal.classList.add('show-modal');
        modal.setAttribute('aria-hidden', 'false');
        modal.querySelector('.success-modal-close')?.focus();
    };

    /**
     * Khởi tạo các cách đóng popup: nút đóng, click nền và phím Escape
     */
    const initializeSuccessModal = () => {
        const modal = document.querySelector(CONFIG.successModalSelector);
        if (!modal) {
            return;
        }

        modal.setAttribute('aria-hidden', 'true');
        modal.querySelector('.success-modal-close')?.addEventListener('click', closeSuccessModal);
        modal.addEventListener('click', (event) => {
            if (event.target === modal) {
                closeSuccessModal();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && modal.classList.contains('show-modal')) {
                closeSuccessModal();
            }
        });
    };

    // Blocked words for content filtering (normalized, no spaces)
    const BLOCKED_WORDS = new Set([
        'conmemay', 'cmm', 'cmay', 'dm', 'dmm', 'dit', 'ditme', 'dime', 'dcm',
        'vl', 'vkl', 'vcc', 'vlon', 'vuto', 'lon', 'buoi', 'cailon', 'calon', 'cl', 'cal', 'cac',
        'thangcho', 'thanglon', 'cho', 'chochet', 'may', 'matday', 'cut', 'dkm', 'dkmm', 'duma',
        'dumamay', 'buoid', 'buom', 'duongvat', 'ditbo', 'balon', 'ml', 'manglon', 'matlon',
        'memay', 'cc', 'cave', 'thangdien', 'nguoixau', 'fuck', 'fucking', 'sex', 'rape', 'chym', 'chich',
        'chocut', 'dog', 'dongvat'
    ]);

    // Blocked phone numbers
    const BLOCKED_PHONES = new Set(['0769218232', '0353676486']);

    /**
     * Normalize text for comparison (remove diacritics and spaces)
     */
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '');
    };

    /**
     * Check if text contains any blocked words
     */
    const containsBlockedWord = (text) => {
        const normalized = normalizeText(text);
        for (const word of BLOCKED_WORDS) {
            if (normalized.includes(word)) return true;
        }
        return false;
    };

    /**
     * Get form field value helper
     */
    const getFieldValue = (form, selector) => {
        const el = form.querySelector(selector);
        return el ? el.value?.trim() || '' : '';
    };

    /**
     * Get form field values
     */
    const getFormData = (form) => ({
        fullname: getFieldValue(form, 'input[name="fullname"]'),
        phone: getFieldValue(form, 'input[name="phone"]'),
        source: getFieldValue(form, 'input[name="source"]'),
        city: getFieldValue(form, 'input[name="city"]'),
        campaign: getFieldValue(form, 'input[name="campaign"]') || '',
        servicekhoa: getFieldValue(form, 'select[name="servicekhoa"]'),
        service: getFieldValue(form, 'select[name="service"]'),
        message: getFieldValue(form, 'textarea[name="message"]' || ''),
        submit_single_form: true
    });

    /**
     * Validate form data and return errors array
     */
    const validateFormData = ({ fullname, phone, service }) => {
        const errors = [];

        if (!fullname) {
            errors.push('Họ và tên không được để trống.');
        } else if (containsBlockedWord(fullname)) {
            errors.push('Họ tên chứa nội dung không phù hợp.');
        }

        if (!phone) {
            errors.push('Số điện thoại không được để trống.');
        } else if (BLOCKED_PHONES.has(phone)) {
            errors.push('Số điện thoại không hợp lệ, quý khách vui lòng chọn lại.');
        }

        if (service === 'Không chọn') {
            errors.push('Vui lòng chọn dịch vụ quan tâm.');
        }

        return errors;
    };

    /**
     * Redirect after delay
     */
    const redirectTo = (url, delay = CONFIG.redirectDelay) => {
        setTimeout(() => { window.location.href = url; }, delay);
    };

    /**
     * Cập nhật trạng thái nút gửi và dùng lại cho mọi loại form
     *
     * @param {HTMLElement|null} button Nút gửi của form.
     * @param {boolean} isSubmitting Trạng thái đang gửi.
     */
    const setSubmitButtonState = (button, isSubmitting) => {
        if (!button) {
            return;
        }

        if (isSubmitting) {
            button.dataset.originalLabel = button.innerHTML;
            button.innerHTML = 'Đang gửi...';

            const spinner = document.createElement('span');
            spinner.className = 'spinner-border spinner-border-sm ms-2';
            spinner.setAttribute('role', 'status');
            spinner.setAttribute('aria-hidden', 'true');
            button.appendChild(spinner);
        } else if (button.dataset.originalLabel) {
            button.innerHTML = button.dataset.originalLabel;
            delete button.dataset.originalLabel;
        }

        button.disabled = isSubmitting;
        button.style.cursor = isSubmitting ? 'not-allowed' : '';
    };

    /**
     * Gửi form báo giá đến endpoint hiện có và yêu cầu response JSON
     *
     * @param {HTMLFormElement} form Form đăng ký báo giá.
     * @return {Promise<Object>} Dữ liệu phản hồi thành công.
     */
    const submitPriceFormAjax = async (form) => {
        const formData = new FormData(form);
        formData.set('submit-popup-form-main', '1');
        formData.set('redirect_url', 'popup-modal');

        const response = await fetch(form.action, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' },
            body: formData
        });
        const data = await response.json();

        if (!response.ok || data?.status !== 'success') {
            throw new Error(data?.message || 'Đăng ký không thành công, vui lòng thử lại.');
        }

        return data;
    };


    /**
     * Khởi tạo form đăng ký tại từng nhóm bảng giá
     *
     * @param {HTMLFormElement} form Form đăng ký báo giá.
     */
    const initializePriceForm = (form) => {
        form.addEventListener('submit', async (event) => {
            event.preventDefault();

            if (form.dataset.isSubmitting === 'true') {
                return;
            }
            if (!form.checkValidity()) {
                form.reportValidity();
                return;
            }

            const formData = {
                fullname: getFieldValue(form, 'input[name="fullName"]'),
                phone: getFieldValue(form, 'input[name="telNumber"]'),
                service: getFieldValue(form, 'input[name="service"]')
            };
            const errors = validateFormData(formData);

            if (errors.length) {
                toastr.error(errors.join('\n'));
                return;
            }

            const submitButton = event.submitter || form.querySelector('[type="submit"]');
            form.dataset.isSubmitting = 'true';
            setSubmitButtonState(submitButton, true);

            try {
                const data = await submitPriceFormAjax(form);
                trackRegistrationSuccess('price_registration');
                form.reset();
                toastr.success(data.message || 'Đăng ký dịch vụ thành công!');
                showSuccessModal(submitButton);
            } catch (error) {
                console.error('Price form submission failed:', error);
                toastr.error(error.message || 'Không thể gửi yêu cầu, vui lòng thử lại sau.');
            } finally {
                form.dataset.isSubmitting = 'false';
                setSubmitButtonState(submitButton, false);
            }
        });
    };

    /**
     * Handle form submission via AJAX
     */
    const submitFormAjax = async (form, formData) => {

        const { ajax_url, action, nonce_name, rest_url } = window.subscribePopupData;
		const freshNonceData = await getFreshNonce(rest_url, nonce_name);
		const nonce = freshNonceData?.nonce
		const newFormData = new FormData();
        // Add action and nonce
        newFormData.append('action', action);
		newFormData.append('nonce', nonce);

        // Create URLSearchParams for form data
		Object.entries(formData).forEach(([key, value]) => {
			newFormData.append(key, value);
		});

        const response = await fetch(ajax_url, {
            method: 'POST',
            headers: { 'X-WP-Nonce': nonce },
            body: newFormData
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    };

    /**
     * Initialize form - remove action and add custom handler
     */
    const initializeForm = (form) => {
        // Remove the form's action attribute to prevent any default submission
        form.removeAttribute('action');

        // Set method to POST (though we won't use native submission)
        form.setAttribute('method', 'POST');

        // Add novalidate to prevent browser validation interfering
        form.setAttribute('novalidate', 'true');

        // Find and modify submit buttons to be type="button" to prevent form submission
        const submitButtons = form.querySelectorAll('button[name="submit_single_form"], input[type="submit"]');
        submitButtons.forEach(btn => {
            if (btn.tagName === 'BUTTON') {
                btn.addEventListener('click', (e) => handleButtonClick(e, form));
            } else if (btn.tagName === 'INPUT') {
                // Create a replacement button
                const newBtn = document.createElement('button');
                newBtn.name = 'submit_single_form';
                newBtn.className = btn.className;
                newBtn.textContent = btn.value || 'Đăng ký';
                newBtn.addEventListener('click', (e) => handleButtonClick(e, form));
                btn.parentNode.replaceChild(newBtn, btn);
            }
        });

        // Also handle Enter key in input fields
        // form.querySelectorAll('input').forEach(input => {
        //     input.addEventListener('keypress', (e) => {
        //         if (e.key === 'Enter') {
        //             e.preventDefault();
        //             handleButtonClick(e, form);
        //         }
        //     });
        // });

        // Add a submit handler as backup to catch any remaining submit events
        form.addEventListener('submit', (e) => {
			console.log('Form submit event caught, preventing default.', e.target);
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
        }, true);
    };

    /**
     * Handle button click (our custom submit)
     */
    const handleButtonClick = async (event, form) => {
        event.preventDefault();
        event.stopPropagation();
        const submitButton = event.currentTarget || event.target;

        // Prevent double submission
        if (form.dataset.isSubmitting === 'true') return;

        const formData = getFormData(form);
        const errors = validateFormData(formData);

        if (errors.length) {
            toastr.error(errors.join('\n'));
            return;
        }

        // Check if subscribePopupData is available
        if (typeof window.subscribePopupData === 'undefined') {
            console.error('subscribePopupData is not defined');
            toastr.error('Lỗi cấu hình, vui lòng tải lại trang.');
            return;
        }

        // Mark as submitting
        form.dataset.isSubmitting = 'true';
        setSubmitButtonState(submitButton, true);

        try {
            const data = await submitFormAjax(form, formData);

            if (data?.status === 'success') {
                toastr.success(data.message || 'Đăng ký thành công!');

                const is_popup_modal = form.dataset.redirectUrl === 'popup-modal';

                const formName = form.matches('.subscribe-form-main-t3')
                    ? 'home_registration'
                    : form.matches('.popup-single-post-form') ? 'post_popup' : 'post_subscription';
                trackRegistrationSuccess(formName, is_popup_modal ? undefined : () => {
                    redirectTo(data.redirect || CONFIG.getSuccessUrl(), 0);
                });

                if (is_popup_modal) {
                    showSuccessModal(submitButton);
                }
            } else {
                toastr.error(data?.message || 'Đăng ký không thành công, vui lòng thử lại.');
            }
        } catch (error) {
            console.error('Form submission failed:', error);
            toastr.error('Không thể gửi yêu cầu, vui lòng thử lại sau.');
        } finally {
            form.dataset.isSubmitting = 'false';
            setSubmitButtonState(submitButton, false);
        }
    };

    // Initialize all target forms
    document.querySelectorAll(CONFIG.selectors).forEach(initializeForm);
    document.querySelectorAll(CONFIG.priceFormSelector).forEach(initializePriceForm);
    initializeSuccessModal();
});
